import { useState, useEffect, useCallback, useRef } from 'react';
import {
  topicLabel, topicMeta,
  eyebrowStyle, h1Style, subtitleStyle, sectionLabelStyle,
  cardStyle, backBtnStyle, inputStyle,
} from './tokens';
import DiscussionCard from './DiscussionCard';
import {
  fetchSortedQuestions, fetchExperienceCounts, humanizeError,
} from '../../lib/communityService';

const PAGE_SIZE = 10;
const SORTS = [
  { key: 'newest',      label: 'Newest' },
  { key: 'trending',    label: 'Trending' },
  { key: 'recommended', label: 'Recommended' },
];

/**
 * DiscussionsListScreen — the shared body used by both:
 *   • TopicScreen (topic-scoped feed reachable by tapping a Browse Topics card)
 *   • AllDiscussionsScreen (unscoped, reachable by "Load more discussions" on Home)
 *
 * Features:
 *   • Search-within-scope (debounced 350ms)
 *   • Sort chips: Newest / Trending / Recommended
 *   • Infinite scroll (IntersectionObserver)
 *   • Restarts pagination on sort or search change
 */
export default function DiscussionsListScreen({ topic = null, onBack, onOpenQuestion }) {
  const meta = topic ? topicMeta(topic) : null;
  const Icon = meta?.Icon;

  const [sort, setSort]         = useState('newest');
  const [rawQ, setRawQ]         = useState('');
  const [search, setSearch]     = useState('');
  const [items, setItems]       = useState([]);
  const [counts, setCounts]     = useState({});
  const [loading, setLoading]   = useState(true);
  const [loadingMore, setLM]    = useState(false);
  const [reachedEnd, setEnd]    = useState(false);
  const [error, setError]       = useState(null);
  const sentinelRef             = useRef(null);

  useEffect(() => {
    const h = setTimeout(() => setSearch(rawQ.trim()), 350);
    return () => clearTimeout(h);
  }, [rawQ]);

  /* first page whenever sort / search / topic change */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null); setEnd(false);
      try {
        const rows = await fetchSortedQuestions({ sort, topic, search, limit: PAGE_SIZE, offset: 0 });
        if (cancelled) return;
        setItems(rows);
        if (rows.length < PAGE_SIZE) setEnd(true);
        setCounts(rows.length ? await fetchExperienceCounts(rows.map(r => r.id)) : {});
      } catch (e) {
        if (!cancelled) { setItems([]); setError(humanizeError(e)); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [sort, topic, search]);

  const loadMore = useCallback(async () => {
    if (loadingMore || reachedEnd || loading || items.length === 0) return;
    setLM(true);
    try {
      const rows = await fetchSortedQuestions({ sort, topic, search, limit: PAGE_SIZE, offset: items.length });
      if (rows.length < PAGE_SIZE) setEnd(true);
      if (rows.length) {
        setItems(prev => [...prev, ...rows]);
        const c = await fetchExperienceCounts(rows.map(r => r.id));
        setCounts(prev => ({ ...prev, ...c }));
      }
    } finally {
      setLM(false);
    }
  }, [items, loadingMore, reachedEnd, loading, sort, topic, search]);

  useEffect(() => {
    if (!sentinelRef.current) return;
    const el = sentinelRef.current;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) loadMore();
    }, { rootMargin: '200px' });
    io.observe(el);
    return () => io.disconnect();
  }, [loadMore]);

  return (
    <div style={{ padding: '20px 16px 60px' }} data-testid={topic ? `topic-screen-${topic}` : 'all-discussions-screen'}>
      <button onClick={onBack} style={backBtnStyle} data-testid="topic-back-btn">← Back</button>

      {/* Header */}
      <header style={{ marginTop: 12, marginBottom: 18 }}>
        <div style={eyebrowStyle}>{topic ? 'TOPIC' : 'BROWSE'}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {Icon && (
            <span
              style={{
                width: 38, height: 38, borderRadius: 12, background: meta.tint,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                border: '1px solid rgba(0,0,0,.05)',
              }}
              data-testid="topic-header-icon"
            >
              <Icon size={20} strokeWidth={1.5} color={meta.stroke} />
            </span>
          )}
          <h1 style={{ ...h1Style, fontSize: 28 }}>
            {topic ? topicLabel(topic) : 'All Discussions'}
          </h1>
        </div>
        {topic && meta?.subtitle && (
          <p style={{ ...subtitleStyle, marginTop: 6 }}>{meta.subtitle}</p>
        )}
      </header>

      {/* Search */}
      <div style={{ ...cardStyle, padding: '4px 8px', marginBottom: 14 }} data-testid="topic-search-card">
        <input
          type="text"
          value={rawQ}
          onChange={(e) => setRawQ(e.target.value)}
          placeholder={topic ? `Search ${topicLabel(topic).toLowerCase()}…` : 'Search all discussions…'}
          data-testid="topic-search-input"
          style={{
            width: '100%', border: 'none', background: 'transparent',
            padding: '12px 10px', fontSize: 15, color: '#232323',
            fontFamily: 'inherit', outline: 'none',
          }}
        />
      </div>

      {/* Sort chips */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 18 }} data-testid="topic-sort-chips" role="tablist">
        {SORTS.map((s) => {
          const on = sort === s.key;
          return (
            <button
              key={s.key}
              onClick={() => setSort(s.key)}
              data-testid={`sort-${s.key}`}
              role="tab"
              aria-selected={on}
              style={{
                flex: 1, padding: '9px 6px', borderRadius: 999, cursor: 'pointer',
                background: on ? 'rgba(109,94,245,.10)' : '#fff',
                border: on ? '1px solid rgba(109,94,245,.30)' : '1px solid rgba(0,0,0,.06)',
                color: on ? '#6D5EF5' : '#232323',
                fontSize: 12.5, fontWeight: 600, fontFamily: 'inherit',
              }}
            >
              {s.label}
            </button>
          );
        })}
      </div>

      {/* Feed */}
      <div style={sectionLabelStyle}>
        {search ? `Results for “${search}”` : `${sort === 'newest' ? 'Newest' : sort === 'trending' ? 'Trending' : 'Recommended'} first`}
      </div>

      {loading ? (
        <div style={{ ...cardStyle, padding: '22px 20px', textAlign: 'center', color: '#8A847C', fontSize: 12.5 }}>Loading…</div>
      ) : error ? (
        <div style={{ ...cardStyle, padding: '22px 20px', textAlign: 'center', color: '#C67A69', fontSize: 12.5 }}>{error}</div>
      ) : items.length === 0 ? (
        <div style={{ ...cardStyle, padding: '28px 20px', textAlign: 'center', color: '#8A847C', fontSize: 13 }}>
          {search ? 'Nothing matches yet.' : 'No discussions here yet.'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {items.map((q) => (
            <DiscussionCard
              key={q.id}
              q={q}
              sharedCount={counts[q.id] || 0}
              onOpen={() => onOpenQuestion(q)}
            />
          ))}
          {!reachedEnd && (
            <div ref={sentinelRef} data-testid="topic-feed-sentinel" style={{ height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8A847C', fontSize: 12 }}>
              {loadingMore ? 'Loading more…' : ''}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
